import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, exhaustMap, filter, map, Observable, of, switchMap, take, tap, throwError, timeout, timer } from 'rxjs';
import { DialogService } from './dialog.service';
import { environment } from '../../environments/environment';
import { createQueryParams } from '../helpers';
import {
  CashRegister,
  FiscalHistory,
  FiscalReturnPreview,
  FiscalOperation,
  FiscalPage,
  FiscalSettings,
  FiscalShift,
  ReceiptPreview,
  RegisterStatus,
} from '../components/prro/prro.types';
import { CardPaymentCreatePayload, CardPaymentsBlock } from '../components/payments/payments.types';
import { FiscalNotificationService } from './fiscal-notification.service';

export interface ReceiptConfirmation extends CardPaymentsBlock {
  payment_id: string;
  fiscal_operation: FiscalOperation;
}

@Injectable({ providedIn: 'root' })
export class VchasnoKasaService {
  private readonly http = inject(HttpClient);
  private readonly notifications = inject(FiscalNotificationService);
  private readonly dialogs = inject(DialogService);
  private readonly openingKeys = new Map<string, string>();
  private readonly openingOperations = new Map<string, FiscalOperation>();
  private request<T>(method: string, path: string, body?: unknown, query?: object): Observable<T> {
    return this.http
      .request<{ data: T }>(method, `${environment.baseURL}/${path}`, {
        body,
        params: query && createQueryParams(query),
      })
      .pipe(map((res) => res.data));
  }
  private api<T>(method: string, path: string, body?: unknown, query?: object): Observable<T> {
    return this.request(method, `integrations/vchasno-kassa/${path}`, body, query);
  }
  registers = (archived = false) => this.api<CashRegister[]>('GET', 'cash-registers', null, { archived });
  settings = () => this.api<FiscalSettings>('GET', 'settings');
  saveSettings = (body: FiscalSettings) =>
    this.api<FiscalSettings>('PUT', 'settings', {
      default_tax_group: body.default_tax_group,
      payment_mappings: body.payment_mappings,
    });
  saveRegister = (id: string | null, body: { name: string; api_token?: string; is_active?: boolean }) =>
    this.api<CashRegister>(id ? 'PUT' : 'POST', id ? `cash-registers/${id}` : 'cash-registers', body);
  archiveRegister = (id: string) => this.api<CashRegister>('DELETE', `cash-registers/${id}`);
  restoreRegister = (id: string) => this.api<CashRegister>('POST', `cash-registers/${id}/restore`);
  status = (id: string) => this.api<RegisterStatus>('GET', `cash-registers/${id}/status`);
  command = (id: string, action: string, key: string) =>
    this.api<FiscalOperation>('POST', `cash-registers/${id}/shifts/${action}`, { idempotency_key: key })
      .pipe(tap(() => this.notifications.requestAccepted()));
  operations = (query: object) => this.api<FiscalPage<FiscalOperation>>('GET', 'operations', null, query);
  operation = (id: string) => this.api<FiscalOperation>('GET', `operations/${id}`);
  retry = (id: string) => this.api<FiscalOperation>('POST', `operations/${id}/retry`)
    .pipe(tap(() => this.notifications.requestAccepted()));

  waitForOperation(id: string): Observable<FiscalOperation> {
    return timer(0, 1000).pipe(
      exhaustMap(() => this.operation(id)),
      filter((operation) => ['succeeded', 'skipped', 'manual_required'].includes(operation.status)),
      take(1),
      timeout({ first: 120_000, with: () => throwError(() => ({ code: 'FISCAL_OPERATION_PROCESSING' })) }),
    );
  }

  ensureOpenShift(registerId: string): Observable<boolean> {
    return this.status(registerId).pipe(switchMap((status) => {
      if (status.result.error || typeof status.is_open !== 'boolean') {
        return throwError(() => ({ code: 'FISCAL_RECEIPT_SHIFT_STATUS_UNAVAILABLE' }));
      }
      if (status.is_open) return of(true);
      return this.dialogs.openConfirm({
        title: 'prro.open_shift', text: 'prro.open_shift_prompt',
        confirmText: 'prro.open_shift', cancelText: 'cancel',
      }).pipe(switchMap((confirmed) => {
        if (!confirmed) return of(false);
        if (!this.openingKeys.has(registerId)) this.openingKeys.set(registerId, crypto.randomUUID());
        const existing = this.openingOperations.get(registerId);
        const opening = existing
          ? this.operation(existing.id).pipe(switchMap((current) => {
              if (['succeeded', 'skipped'].includes(current.status)) {
                this.openingKeys.set(registerId, crypto.randomUUID());
                return this.command(registerId, 'open', this.openingKeys.get(registerId)!);
              }
              return current.status === 'manual_required' ? this.retry(current.id) : of(current);
            }))
          : this.command(registerId, 'open', this.openingKeys.get(registerId)!);
        return opening.pipe(
          tap((operation) => this.openingOperations.set(registerId, operation)),
          switchMap((operation) => this.waitForOperation(operation.id)),
          switchMap((operation) => {
            if (!['succeeded', 'skipped'].includes(operation.status)) return this.operationFailure(operation);
            return this.status(registerId);
          }),
          map((current) => {
            if (current.result.error || current.is_open !== true) throw { code: 'FISCAL_OPEN_SHIFT_REQUIRED' };
            this.openingKeys.delete(registerId);
            this.openingOperations.delete(registerId);
            return true;
          }),
        );
      }));
    }));
  }

  retryReceipt(operation: FiscalOperation): Observable<FiscalOperation> {
    return this.operation(operation.id).pipe(switchMap((current) => {
      if (current.status !== 'manual_required') return of(current);
      const code = current.error?.['code'];
      if (['FISCAL_OPEN_SHIFT_REQUIRED', 'FISCAL_RECEIPT_SHIFT_STATUS_UNAVAILABLE'].includes(String(code))) {
        return this.ensureOpenShift(current.cash_register_id).pipe(
          switchMap((opened) => opened ? this.retry(current.id) : EMPTY),
        );
      }
      return this.retry(current.id);
    }));
  }

  waitForReceipt(id: string): Observable<FiscalOperation> {
    return this.waitForOperation(id).pipe(switchMap((operation) => {
      if (operation.status === 'succeeded') return of(operation);
      if (operation.error?.['code'] === 'FISCAL_OPEN_SHIFT_REQUIRED') {
        return this.retryReceipt(operation).pipe(
          switchMap((retry) => this.waitForOperation(retry.id)),
          switchMap((result) => result.status === 'succeeded' ? of(result) : this.operationFailure(result)),
        );
      }
      return this.operationFailure(operation);
    }));
  }

  private operationFailure(operation: FiscalOperation): Observable<never> {
    const code = String(operation.error?.['code'] ?? 'FISCAL_OPERATION_MANUAL_FIX_REQUIRED');
    return throwError(() => ({ code, details: operation.error?.['details'] }));
  }
  shifts = (query: object) => this.api<FiscalPage<FiscalShift>>('GET', 'shifts', null, query);
  history = (id: string, page: number, shiftId?: string) =>
    this.api<FiscalHistory>('GET', `cash-registers/${id}/shifts${shiftId ? `/${shiftId}/checks` : ''}`, null, {
      page,
      take: 25,
    });
  availability = (cardId: string) =>
    this.request<{ connected: boolean }>('GET', `board/card/${cardId}/payments/fiscal-availability`);
  preview = (cardId: string, registerId: string) =>
    this.request<ReceiptPreview>('GET', `board/card/${cardId}/payments/receipt-preview`, null, {
      cash_register_id: registerId,
    });
  confirm = (
    cardId: string,
    paymentId: string | null,
    fields: CardPaymentCreatePayload,
    registerId: string,
    key: string,
    metadata: Record<string, string>,
    chatId: string | null,
    previewRevision: string,
  ) =>
    this.request<ReceiptConfirmation>(
      'POST',
      paymentId
        ? `board/payments/${paymentId}/confirm-with-receipt`
        : `board/card/${cardId}/payments/confirm-with-receipt`,
      {
        ...(!paymentId
          ? {
              payment_type_id: fields.payment_type_id,
              payment_service_id: fields.payment_service_id,
              amount: fields.amount,
              comment: fields.comment,
              idempotency_key: key,
            }
          : {}),
        paid_at: fields.paid_at,
        cash_register_id: registerId,
        preview_revision: previewRevision,
        preview_amount: fields.amount,
        card_metadata: metadata,
        receipt_chat_id: chatId,
      },
    ).pipe(tap(() => this.notifications.requestAccepted()));
  retryDelivery = (paymentId: string) => this.request<void>('POST', `chats/receipt-delivery/${paymentId}/retry`)
    .pipe(tap(() => this.notifications.requestAccepted(true)));
  returnPreview = (paymentId: string) =>
    this.request<FiscalReturnPreview>('GET', `board/payments/${paymentId}/return-preview`);
  createReturn = (paymentId: string, body: { idempotency_key: string; rows?: { index: number; quantity: number }[]; amount?: number }) =>
    this.request<FiscalOperation>('POST', `board/payments/${paymentId}/return-with-receipt`, body)
      .pipe(tap(() => this.notifications.requestAccepted()));
}
